import React, { FC, useCallback, useRef, useState } from 'react';

import { Button, ButtonSize, ButtonTheme } from './Button';
import { Popover } from '../components/Popover/Popover';
import { ButtonGroup, GroupButton } from './ButtonGroup';
import { HelpTooltip } from './HelpTooltip';

import MucPriceButtonsImage from '../../../img/muc-price-buttons.svg';
import './ChangePriceDialog.scss';
import { useListContentChecks } from './useListContentChecks';
import { BulkActionResponse } from '../../store/api';
import { PriceTier } from '../../../models/PriceTier';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { CheckBeforeAddingModal } from './CheckBeforeAddingModal';
import { UnavailableProductsModal } from './UnavailableProductsModal';

interface Props {
    readonly onChange: (priceTier: PriceTier, performChecks: boolean) => Promise<BulkActionResponse>;
    readonly size?: ButtonSize;
    readonly isNothingSelected?: boolean;
}

export const ChangePriceDialog: FC<Props> = ({ onChange, size = ButtonSize.SMALL, isNothingSelected = false }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleChange = useCallback(
        (performChecks: boolean, { priceTier }: { priceTier: PriceTier }) => {
            setExpanded(false);
            return onChange(priceTier, performChecks);
        },
        [onChange]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<{
        priceTier: PriceTier;
    }>({ onAction: handleChange });

    const checkBeforeAddingModalProps = getCheckBeforeAddingModalProps();

    return (
        <>
            <Button
                icon={'swap_horiz'}
                className={classNames('change-price-dialog-toggle')}
                size={size}
                theme={ButtonTheme.NEUTRAL}
                onClick={() => setExpanded(it => !it)}
                ref={buttonRef}
                disabled={checkBeforeAddingModalProps.isLoading || isNothingSelected}
                isLoading={checkBeforeAddingModalProps.isLoading}
            >
                <span>{t('lendingModel.change.change')}</span>
                {/* TODO [i18n] css change following img tag in conditional and provide language specific img*/}
                <img src={MucPriceButtonsImage} aria-hidden={true} alt="Change Price" />
            </Button>
            <CheckBeforeAddingModal
                {...checkBeforeAddingModalProps}
                customTitle={t('components.checkBeforeAddingModal.customTitles.checkBeforeChanging')}
            />
            <UnavailableProductsModal
                {...getUnavailableProductsModalProps()}
                customTitle={t('notifications.unavailableProductsModal.notChanged.title')}
                customMessage={
                    <Trans
                        i18nKey="notifications.unavailableProductsModal.notChanged.message"
                        components={{
                            br: <br />
                        }}
                    />
                }
            />
            <Popover
                className="change-price-dialog"
                target={buttonRef}
                visible={expanded}
                onClose={setExpanded}
                placement="bottom"
            >
                <p className="change-price-dialog-info">{t('lendingModel.change.changeMessage')}:</p>
                <div className="change-price-dialog-buttons">
                    <ButtonGroup size="lg" boldSublabel={true}>
                        <GroupButton
                            label={t('lendingModel.ocou')}
                            onClick={() => changePrice({ priceTier: { lendingModel: 'OCOU', level: 'MIN' } })}
                        />
                        <GroupButton
                            label={t('lendingModel.sim')}
                            subLabel={t('lendingModel.low')}
                            onClick={() => changePrice({ priceTier: { lendingModel: 'SIM', level: 'MIN' } })}
                        />
                        <GroupButton
                            label={t('lendingModel.sim')}
                            subLabel={t('lendingModel.medium')}
                            onClick={() => changePrice({ priceTier: { lendingModel: 'SIM', level: 'MEDIUM' } })}
                        />
                        <GroupButton
                            label={t('lendingModel.sim')}
                            subLabel={t('lendingModel.high')}
                            onClick={() => changePrice({ priceTier: { lendingModel: 'SIM', level: 'MAX' } })}
                        />
                    </ButtonGroup>
                    <HelpTooltip size="large">
                        <Trans i18nKey="lendingModel.change.changeTooltip" components={[<p />, <div />]} />
                    </HelpTooltip>
                </div>
            </Popover>
        </>
    );
};
